/**
 * Belge çıkarımı orkestrasyonu.
 *
 * PDF → Web Worker (pdf.js), DOCX → mammoth, LaTeX → kaynak ayrıştırıcı.
 * Hepsi tarayıcıda çalışır; dosya hiçbir sunucuya gönderilmez.
 */

import type { ExtractedDocument, ExtractionQuality, TextBlock } from '../types';
import { assessQuality } from '../text/quality';
import { RepError } from '../errors';
import { extractLatex } from './latex';


export type { ExtractionProgress, ProgressFn } from './types';
import type { ExtractionProgress, ProgressFn } from './types';

export type SupportedFormat = 'pdf' | 'docx' | 'latex';

export function detectFormat(fileName: string, mime?: string): SupportedFormat | null {
	const lower = fileName.toLowerCase();
	if (lower.endsWith('.pdf') || mime === 'application/pdf') return 'pdf';
	if (
		lower.endsWith('.docx') ||
		mime === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
	)
		return 'docx';
	if (lower.endsWith('.doc') || mime === 'application/msword') return null; // mammoth .doc okuyamaz
	if (/\.(tex|ltx|latex)$/.test(lower) || mime === 'application/x-tex' || mime === 'text/x-tex')
		return 'latex';
	return null;
}

export function computeBlockStarts(blocks: TextBlock[]): number[] {
	const starts: number[] = [];
	let cursor = 0;
	for (const b of blocks) {
		starts.push(cursor);
		cursor += b.text.length + 2;
	}
	return starts;
}

export async function extractDocument(
	file: File,
	onProgress: ProgressFn = () => {}
): Promise<ExtractedDocument> {
	const format = detectFormat(file.name, file.type);
	if (!format) {
		throw new RepError('err.unsupported');
	}

	if (format === 'pdf') return extractPdf(file, onProgress);
	if (format === 'docx') {
		// mammoth yalnızca DOCX yüklendiğinde indirilsin (ilk yükü küçültür)
		onProgress({ stageKey: 'proc.stage.docxLoader', ratio: 0.03 });
		const { extractDocx } = await import('./docx');
		return extractDocx(file, onProgress);
	}
	return extractLatexFile(file, onProgress);
}

/* -------------------------------------------------------------------------- */
/* PDF                                                                         */
/* -------------------------------------------------------------------------- */

interface PdfDoneMessage {
	type: 'done';
	pages: number;
	blocks: TextBlock[];
	meta: Record<string, string>;
	emptyPages: number;
	warnings: string[];
}

async function extractPdf(file: File, onProgress: ProgressFn): Promise<ExtractedDocument> {
	onProgress({ stageKey: 'proc.stage.pdfReading', ratio: 0.02 });
	const buffer = await file.arrayBuffer();

	let result: PdfDoneMessage | null = null;
	let worker: Worker | null = null;

	try {
		worker = new Worker(new URL('./pdf.worker.ts', import.meta.url), { type: 'module' });
		result = await new Promise<PdfDoneMessage>((resolve, reject) => {
			worker!.onmessage = (ev: MessageEvent) => {
				const msg = ev.data;
				if (msg?.type === 'progress') {
					onProgress({
						stageKey: msg.stageKey,
					stageParams: msg.stageParams,
						page: msg.page,
						total: msg.total,
						ratio: 0.05 + 0.85 * (msg.page / Math.max(1, msg.total))
					});
				} else if (msg?.type === 'done') {
					resolve(msg as PdfDoneMessage);
				} else if (msg?.type === 'error') {
					reject(new RepError('err.pdf', { message: msg.message }));
				}
			};
			worker!.onerror = (ev) => {
				reject(new RepError('err.pdfWorker', { message: ev.message || '' }));
			};
			worker!.postMessage({ type: 'extract', file: buffer, fileName: file.name }, [buffer]);
		});
	} catch (err) {
		const message = err instanceof Error ? err.message : String(err);
		// Worker kısıtlanmışsa (bazı gömülü tarayıcılar) ana thread'e düş
		if (/worker/i.test(message)) {
			onProgress({ stageKey: 'proc.stage.workerFallback', ratio: 0.1 });
			const { extractPdfMainThread } = await import('./pdf.main');
			result = await extractPdfMainThread(buffer, onProgress);
		} else {
			throw err;
		}
	} finally {
		worker?.terminate();
	}

	if (!result) throw new RepError('err.pdfIncomplete');

	const blocks = result.blocks.filter((b) => b.text.trim().length > 0);
	const text = blocks.map((b) => b.text).join('\n\n');
	const quality = assessQuality({
		text,
		chars: text.length,
		pages: result.pages,
		emptyPages: result.emptyPages,
		notes: result.warnings,
		format: 'pdf'
	});

	return {
		fileName: file.name,
		format: 'pdf',
		fileSize: file.size,
		blocks,
		text,
		blockStarts: computeBlockStarts(blocks),
		pageCount: result.pages,
		meta: {
			title: result.meta.Title,
			author: result.meta.Author,
			producer: result.meta.Producer,
			creationDate: result.meta.CreationDate
		},
		quality,
		warnings: result.warnings
	};
}

/* -------------------------------------------------------------------------- */
/* LaTeX                                                                       */
/* -------------------------------------------------------------------------- */

async function extractLatexFile(file: File, onProgress: ProgressFn): Promise<ExtractedDocument> {
	onProgress({ stageKey: 'proc.stage.latexRead', ratio: 0.2 });
	const raw = await file.text();
	onProgress({ stageKey: 'proc.stage.latexParse', ratio: 0.5 });
	const { blocks, meta, warnings } = extractLatex(raw);
	const text = blocks.map((b) => b.text).join('\n\n');
	const quality: ExtractionQuality = assessQuality({
		text,
		chars: text.length,
		pages: 0,
		emptyPages: 0,
		notes: warnings,
		format: 'latex'
	});
	onProgress({ stageKey: 'proc.stage.done', ratio: 1 });
	return {
		fileName: file.name,
		format: 'latex',
		fileSize: file.size,
		blocks,
		text,
		blockStarts: computeBlockStarts(blocks),
		pageCount: 0,
		meta,
		quality,
		warnings
	};
}
